import { useState, useMemo, useEffect } from "react";
import api from "api/api";
import { Link } from "react-router-dom";

//Props Validations
import PropTypes from "prop-types";

//Material UI Components
import Switch from "@mui/material/Switch";
import { styled } from "@mui/material/styles";

// import mui icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FiltersHeaderLabel from "components/common/FiltersHeaderLabel";

//import component
import Loader from "../components/common/Loader";
import Header from "../components/common/Header";

//other packages
import { AgGridReact } from "ag-grid-react";
import FullScreenConatiner from "components/layouts/FullScreenConatiner";

//redux
import { useSelector } from "react-redux";

const IOSSwitch = styled((props) => (
    <Switch
        focusVisibleClassName=".Mui-focusVisible"
        disableRipple
        {...props}
    />
))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: 2,
        transitionDuration: "300ms",
        "&.Mui-checked": {
            transform: "translateX(16px)",
            color: "#fff",
            "& + .MuiSwitch-track": {
                backgroundColor: theme.palette.mode === "dark" ? "#005EEF" : "#005EEF",
                opacity: 1,
                border: 0,
            },
            "&.Mui-disabled + .MuiSwitch-track": {
                opacity: 0.5,
            },
        },
        "&.Mui-focusVisible .MuiSwitch-thumb": {
            color: "#33cf4d",
            border: "6px solid #fff",
        },
        "&.Mui-disabled .MuiSwitch-thumb": {
            color: theme.palette.mode === "light" ? theme.palette.grey[100] : theme.palette.grey[600],
        },
        "&.Mui-disabled + .MuiSwitch-track": {
            opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
        },
    },
    "& .MuiSwitch-thumb": {
        boxSizing: "border-box",
        width: 22,
        height: 22,
    },
    "& .MuiSwitch-track": {
        borderRadius: 26 / 2,
        backgroundColor: theme.palette.mode === "light" ? "grey" : "#005EEF",
        opacity: 1,
        transition: theme.transitions.create(["background-color"], {
            duration: 500,
        }),
    },
}));

function UserManagement({ headerTitle = "" }) {
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;

    const [users, setUsers] = useState([]);
    const [roleChanged, setRoleChanged] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [rolesData, setRolesData] = useState([]);
    const columnDefs = [
        {
            headerName: "Name",
            field: "email",
            cellRenderer: (params) => {
                let namePart = params.data.email.split("@");
                let finalName = titleCase(namePart[0].replaceAll(".", " "));
                return <div>{finalName}</div>;
            },
        },
        {
            headerName: "Email ID",
            field: "email",
        },
        {
            headerName: "Role",
            field: "role",
            cellRenderer: ({ data }) => (
                <div>
                    <span className={data.role__title.toLowerCase() === "user" ? "color-blue text-bold-6" : ""}>User</span>
                    <IOSSwitch
                        sx={{ m: 1 }}
                        checked={data.role__title.toLowerCase() === "admin"}
                        onChange={handleRoleChange(data)}
                    />
                    <span className={data.role__title.toLowerCase() === "admin" ? "color-blue text-bold-6" : ""}>Admin</span>
                </div>
            ),
        },
    ];

    useEffect(() => {
        async function fetchData() {
            setIsLoading(true);
            const url = process.env.REACT_APP_SERVER_PATH + `/user-product-category-roles/all/?product_category=${productCategoryId}&ordering=-id`;
            api.get(url)
                .then(async (res) => {
                    setUsers(res.data);
                    setRoleChanged(false);
                    setIsLoading(false);
                })
                .catch(async (error) => {
                    console.error("Error", error);
                    setIsLoading(false);
                });
        }

        fetchData();
    }, [roleChanged, productCategoryId]);

    useEffect(() => {
        async function fetchData() {
            setIsLoading(true);
            const url = process.env.REACT_APP_SERVER_PATH + `/roles/all/`;
            api.get(url)
                .then(async (res) => {
                    setRolesData(res.data);
                    setIsLoading(false);
                })
                .catch(async (error) => {
                    console.error("Error", error);
                    setIsLoading(false);
                });
        }

        fetchData();
    }, []);

    function titleCase(str) {
        var splitStr = str.toLowerCase().split(" ");
        for (var i = 0; i < splitStr.length; i++) {
            // You do not need to check if i is larger than splitStr length, as your for does that for you
            // Assign it back to the array
            splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
        }
        // Directly return the joined string
        return splitStr.join(" ");
    }

    const handleRoleChange = (data) => (event) => {
        setIsLoading(true);
        const url = process.env.REACT_APP_SERVER_PATH + `/user-product-category-roles/${data.id}/`;
        const formData = new FormData();
        const roleId = rolesData.find((role) => role.title.toLowerCase() === (event.target.checked ? "admin" : "user")).id;
        formData.append("role", roleId);
        // Send an API request to update the user's role
        api.patch(url, formData)
            .then(() => {
                setRoleChanged(true);
                setRoleChanged(true);
            })
            .catch((error) => {
                setRoleChanged(true);
                console.error("Error updating user role:", error);
                // Revert the UI state in case of an error
                setIsLoading(false);
            });
    };

    const sortingOrder = useMemo(() => {
        return ["desc", "asc", null];
    }, []);

    const defaultColDef = useMemo(() => {
        return {
            editable: false,
            suppressMenu: true,
            sortable: true,
            filter: true,
            resizable: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"], // to get filter remove class .ag-floating-filter-button  form css
                // filterOptions: ["equals", "contains", "startsWith", "endsWith"], // to get filter remove class .ag-floating-filter-button  form css
            },
            cellStyle: { fontSize: "12px", border: "none" },
            minWidth: 490,
        };
    }, []);

    return (
        <div>
            {isLoading && <Loader />}
            <Header title={headerTitle} />
            <div
                className="ms-4 me-4 mb-0 mt-3 background-color-white box-shadow--2 d-flex"
                style={{
                    borderRadius: "5px",
                }}
            >
                <FiltersHeaderLabel />
            </div>

            <FullScreenConatiner
                style={{
                    padding: "20px",
                    paddingTop: 0,
                }}
                subHeight={150}
            >
                <div
                    className=""
                    style={{
                        width: 150,
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <Link to="/studies">
                        <div
                            className="p-2 liner-light-blue-background color-white"
                            style={{
                                borderRadius: "50%",
                                position: "relative",
                                top: -15,
                                left: -35,
                                cursor: "pointer",
                            }}
                        >
                            <span>
                                {/* <Link to="/studies"> */}
                                <ArrowBackIcon className="color-white" />
                                {/* </Link> */}
                            </span>
                        </div>
                    </Link>
                </div>

                <div className="landing-page-table mb-0">
                    <div
                        className="ag-theme-alpine"
                        style={{ height: "calc(100vh - 205px)", width: "100%" }}
                    >
                        <AgGridReact
                            rowStyle={{
                                background: "white",
                            }}
                            defaultColDef={defaultColDef}
                            rowData={users}
                            columnDefs={columnDefs}
                            onGridReady={(params) => params.id}
                            // sideBar={true}
                            rowHeight={38}
                            headerHeight={40}
                            suppressDragLeaveHidesColumns={true}
                            sortingOrder={sortingOrder}
                        ></AgGridReact>
                    </div>
                </div>
            </FullScreenConatiner>
        </div>
    );
}

// Typechecking props for the UserManagement
UserManagement.propTypes = {
    headerTitle: PropTypes.string,
};

export default UserManagement;
